// src/admin/PageManagement.js
import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import {
  Save, AlertCircle, CheckCircle, Eye, Plus, Trash2,
  Leaf, Heart, Shield, TrendingUp, Users, Award, Star, Target
} from 'lucide-react';
import SuccessModal from '../components/SuccessModal';

const PageManagement = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showSuccess, setShowSuccess] = useState(false);
  const [activeTab, setActiveTab] = useState('about');

  const defaultAbout = {
    active: true,
    heroTitle: 'About Organic Land',
    heroSubtitle: 'Fresh. Organic. Trusted.',
    heroDescription: 'Hum Pakistan ke sab se behtareen organic products deliver karte hain — directly farms se aapke ghar tak.',
    storyTitle: 'Our Story',
    storyText: 'Organic Land ki shuruaat 2020 mein hui thi, jab humne socha ke logon ko fresh, chemical-free products milne chahiye. Aaj hum 5000+ customers ko serve kar rahe hain.',
    missionTitle: 'Our Mission',
    missionText: 'Har ghar tak fresh organic products pohanchana — affordable price par, quality ke saath.',
    visionTitle: 'Our Vision',
    visionText: 'Pakistan ka sab se trusted organic brand banna.',
    stats: [
      { value: '5,000+', label: 'Happy Customers' },
      { value: '500+', label: 'Organic Products' },
      { value: '50+', label: 'Partner Farms' },
      { value: '4.9', label: 'Rating' },
    ],
    values: [
      { icon: 'leaf', title: '100% Organic', description: 'Certified organic products' },
      { icon: 'heart', title: 'Customer First', description: 'Aapki satisfaction hamari priority' },
      { icon: 'shield', title: 'Trusted Quality', description: 'Har product quality checked' },
      { icon: 'trending', title: 'Always Fresh', description: 'Farm to home delivery' },
    ],
  };

  const [aboutConfig, setAboutConfig] = useState(defaultAbout);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const docRef = doc(db, 'settings', 'about-page');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setAboutConfig({ ...defaultAbout, ...docSnap.data() });
        }
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      await setDoc(doc(db, 'settings', 'about-page'), aboutConfig, { merge: true });
      setMessage({ type: 'success', text: 'About page updated!' });
      setShowSuccess(true);
      window.dispatchEvent(new Event('aboutPageUpdated'));
    } catch (error) {
      console.error('Save error:', error);
      setMessage({ type: 'error', text: 'Failed to save.' });
    } finally {
      setSaving(false);
    }
  };

  const addStat = () => {
    setAboutConfig({
      ...aboutConfig,
      stats: [...(aboutConfig.stats || []), { value: '', label: '' }],
    });
  };

  const updateStat = (i, field, value) => {
    const updated = [...aboutConfig.stats];
    updated[i][field] = value;
    setAboutConfig({ ...aboutConfig, stats: updated });
  };

  const removeStat = (i) => {
    setAboutConfig({
      ...aboutConfig,
      stats: aboutConfig.stats.filter((_, idx) => idx !== i),
    });
  };

  const addValue = () => {
    setAboutConfig({
      ...aboutConfig,
      values: [...(aboutConfig.values || []), { icon: 'leaf', title: '', description: '' }],
    });
  };

  const updateValue = (i, field, value) => {
    const updated = [...aboutConfig.values];
    updated[i][field] = value;
    setAboutConfig({ ...aboutConfig, values: updated });
  };

  const removeValue = (i) => {
    setAboutConfig({
      ...aboutConfig,
      values: aboutConfig.values.filter((_, idx) => idx !== i),
    });
  };

  const iconOptions = [
    { value: 'leaf', label: '🌿 Leaf', Icon: Leaf },
    { value: 'heart', label: '❤️ Heart', Icon: Heart },
    { value: 'shield', label: '🛡️ Shield', Icon: Shield },
    { value: 'trending', label: '📈 Trending', Icon: TrendingUp },
    { value: 'users', label: '👥 Users', Icon: Users },
    { value: 'award', label: '🏆 Award', Icon: Award },
    { value: 'star', label: '⭐ Star', Icon: Star },
    { value: 'target', label: '🎯 Target', Icon: Target },
  ];

  if (loading) {
    return (
      <div className="text-center py-16">
        <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-green-700 border-t-transparent"></div>
        <p className="mt-4 text-gray-500">Loading...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl">

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Leaf className="text-green-700" size={26} /> Pages Management
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            About page ka content yahan se edit karein.
          </p>
        </div>
        <div className="flex gap-2">
          <a href="/about" target="_blank" rel="noopener noreferrer"
            className="bg-blue-50 hover:bg-blue-100 text-blue-700 px-4 py-2.5 rounded-lg transition flex items-center gap-2 font-semibold border border-blue-200 text-sm">
            <Eye size={16} /> View About
          </a>
        </div>
      </div>

      {message.text && (
        <div className={`text-sm p-3 rounded-lg flex items-center gap-2 ${
          message.type === 'success'
            ? 'bg-green-50 text-green-700 border border-green-200'
            : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {message.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
          {message.text}
        </div>
      )}

      <form onSubmit={handleSave} className="space-y-6">

        {/* Visibility */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <label className="flex items-center gap-3 cursor-pointer p-4 rounded-lg bg-green-50 border border-green-200">
            <input type="checkbox" checked={aboutConfig.active}
              onChange={(e) => setAboutConfig({ ...aboutConfig, active: e.target.checked })}
              className="w-5 h-5 text-green-700" />
            <div>
              <span className="text-sm font-semibold text-gray-800 block">Show About Page</span>
              <span className="text-xs text-gray-600">Uncheck karne par page hide ho jayega</span>
            </div>
          </label>
        </div>

        {/* Hero */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-5">Hero Section</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Hero Title</label>
              <input type="text" value={aboutConfig.heroTitle}
                onChange={(e) => setAboutConfig({ ...aboutConfig, heroTitle: e.target.value })}
                className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Hero Subtitle</label>
              <input type="text" value={aboutConfig.heroSubtitle}
                onChange={(e) => setAboutConfig({ ...aboutConfig, heroSubtitle: e.target.value })}
                className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Hero Description</label>
              <textarea value={aboutConfig.heroDescription}
                onChange={(e) => setAboutConfig({ ...aboutConfig, heroDescription: e.target.value })}
                rows={2}
                className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm resize-none" />
            </div>
          </div>
        </div>

        {/* Story */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-5">Our Story</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Story Title</label>
              <input type="text" value={aboutConfig.storyTitle}
                onChange={(e) => setAboutConfig({ ...aboutConfig, storyTitle: e.target.value })}
                className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Story Text</label>
              <textarea value={aboutConfig.storyText}
                onChange={(e) => setAboutConfig({ ...aboutConfig, storyText: e.target.value })}
                rows={4}
                className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm resize-none" />
            </div>
          </div>
        </div>

        {/* Mission + Vision */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-5">Mission & Vision</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Mission Title</label>
                <input type="text" value={aboutConfig.missionTitle}
                  onChange={(e) => setAboutConfig({ ...aboutConfig, missionTitle: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Mission Text</label>
                <textarea value={aboutConfig.missionText}
                  onChange={(e) => setAboutConfig({ ...aboutConfig, missionText: e.target.value })}
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm resize-none" />
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Vision Title</label>
                <input type="text" value={aboutConfig.visionTitle}
                  onChange={(e) => setAboutConfig({ ...aboutConfig, visionTitle: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Vision Text</label>
                <textarea value={aboutConfig.visionText}
                  onChange={(e) => setAboutConfig({ ...aboutConfig, visionText: e.target.value })}
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm resize-none" />
              </div>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-bold text-gray-800">Stats (Numbers)</h2>
            <button type="button" onClick={addStat}
              className="text-sm font-semibold text-green-700 hover:text-green-800 flex items-center gap-1">
              <Plus size={14} /> Add Stat
            </button>
          </div>
          <div className="space-y-3">
            {(aboutConfig.stats || []).map((stat, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 p-3 bg-gray-50 rounded-lg">
                <input type="text" value={stat.value}
                  onChange={(e) => updateStat(i, 'value', e.target.value)}
                  className="col-span-4 border border-gray-300 rounded-lg py-2 px-3 text-sm focus:outline-none focus:border-green-600"
                  placeholder="e.g., 5000+" />
                <input type="text" value={stat.label}
                  onChange={(e) => updateStat(i, 'label', e.target.value)}
                  className="col-span-7 border border-gray-300 rounded-lg py-2 px-3 text-sm focus:outline-none focus:border-green-600"
                  placeholder="e.g., Happy Customers" />
                <button type="button" onClick={() => removeStat(i)}
                  className="col-span-1 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg flex items-center justify-center">
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Values */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-bold text-gray-800">Our Values</h2>
            <button type="button" onClick={addValue}
              className="text-sm font-semibold text-green-700 hover:text-green-800 flex items-center gap-1">
              <Plus size={14} /> Add Value
            </button>
          </div>
          <div className="space-y-4">
            {(aboutConfig.values || []).map((val, i) => {
              const IconOpt = iconOptions.find(o => o.value === val.icon);
              const IconComp = IconOpt ? IconOpt.Icon : Leaf;
              return (
                <div key={i} className="p-4 rounded-xl border border-gray-200 bg-gray-50">
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-green-100 shrink-0">
                      <IconComp size={22} className="text-green-700" />
                    </div>
                    <div className="flex-1 space-y-3">
                      <select value={val.icon}
                        onChange={(e) => updateValue(i, 'icon', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg py-2 px-3 text-sm focus:outline-none focus:border-green-600 bg-white">
                        {iconOptions.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                      <input type="text" value={val.title}
                        onChange={(e) => updateValue(i, 'title', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg py-2 px-3 text-sm focus:outline-none focus:border-green-600"
                        placeholder="Title" />
                      <input type="text" value={val.description}
                        onChange={(e) => updateValue(i, 'description', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg py-2 px-3 text-sm focus:outline-none focus:border-green-600"
                        placeholder="Description" />
                    </div>
                    <button type="button" onClick={() => removeValue(i)}
                      className="bg-red-50 hover:bg-red-100 text-red-600 rounded-lg p-2 shrink-0">
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Save */}
        <div className="flex justify-end sticky bottom-4">
          <button type="submit" disabled={saving}
            className={`bg-green-700 text-white py-3 px-8 rounded-lg font-semibold hover:bg-green-800 transition flex items-center gap-2 shadow-xl ${
              saving ? 'opacity-70 cursor-not-allowed' : ''
            }`}>
            <Save size={18} /> {saving ? 'Saving...' : 'Save About Page'}
          </button>
        </div>

      </form>

      <SuccessModal
        isOpen={showSuccess}
        onClose={() => setShowSuccess(false)}
        title="Success!"
        message="About page updated!"
        buttonText="Great!"
      />

    </div>
  );
};

export default PageManagement;